import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Typography,
  Box,
  IconButton,
  Tooltip,
  TableSortLabel,
  TextField,
  InputAdornment,
  MenuItem,
  Grid
} from '@mui/material';
import {
  Search,
  Refresh,
  Clear,
  Download
} from '@mui/icons-material';

const DataTable = ({
  columns,
  data,
  loading = false,
  total = 0,
  page = 0,
  rowsPerPage = 25,
  onPageChange,
  onRowsPerPageChange,
  onRefresh,
  onSearch,
  onExport,
  filters = [],
  filterValues = {},
  onFilterChange,
  title,
  actions,
  emptyMessage = 'No data found',
  showSearch = true,
  showRefresh = true,
  showExport = true,
  showFilters = true,
  rowsPerPageOptions = [10, 25, 50, 100],
  sortable = true,
  onSort,
  sortField,
  sortDirection,
  hideToolbar = false,
  fullHeight = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearch = (value) => {
    setSearchTerm(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    if (onSearch) {
      onSearch('');
    }
  };

  const handleSort = (field) => {
    if (onSort) {
      const isAsc = sortField === field && sortDirection === 'asc';
      onSort(field, isAsc ? 'desc' : 'asc');
    }
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', ...(fullHeight ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}) }}>
      {/* Toolbar */}
      {!hideToolbar && <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
        <Grid container spacing={2} alignItems="center">
          {title && (
            <Grid item xs={12} md={3}>
              <Typography variant="h6" fontWeight={600}>
                {title}
              </Typography>
            </Grid>
          )}
          
          {showSearch && (
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleClearSearch}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          )}

          {showFilters && filters.length > 0 && (
            <Grid item xs={12} md={3}>
              <Grid container spacing={1}>
                {filters.map((filter) => (
                  <Grid item xs={12} key={filter.key}>
                    <TextField
                      fullWidth
                      select
                      label={filter.label}
                      value={filterValues[filter.key] || ''}
                      onChange={(e) => onFilterChange && onFilterChange(filter.key, e.target.value)}
                      size="small"
                    >
                      <MenuItem value="">All</MenuItem>
                      {filter.options.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}

          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              {showRefresh && onRefresh && (
                <Tooltip title="Refresh">
                  <span>
                    <IconButton onClick={onRefresh} disabled={loading}>
                      <Refresh />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {showExport && onExport && (
                <Tooltip title="Export">
                  <span>
                    <IconButton onClick={onExport} disabled={loading}>
                      <Download />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {actions && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {actions}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>}

      {/* Table */}
      <TableContainer sx={fullHeight ? { flex: 1, overflow: 'auto' } : { maxHeight: 'calc(100vh - 300px)' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align || 'left'}
                  sx={{
                    minWidth: column.minWidth || 100,
                    fontWeight: 600,
                    backgroundColor: '#f8fafc'
                  }}
                >
                  {sortable && column.sortable !== false ? (
                    <TableSortLabel
                      active={sortField === column.key}
                      direction={sortField === column.key ? sortDirection : 'asc'}
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => (
                <TableRow key={row.id || index} hover>
                  {columns.map((column) => (
                    <TableCell
                      key={`${row.id || index}-${column.key}`}
                      align={column.align || 'left'}
                    >
                      {column.render ? (
                        column.render(row[column.key], row)
                      ) : (
                        row[column.key] || '-'
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Paper>
  );
};

export default DataTable;